import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { toast } from "react-toastify";
import { writeFileXLSX, utils } from "xlsx";
import {
  Button,
  ContentBox,
  ContentPage,
  DropdownAction,
  FullTable,
  InputCopy,
  ModalDefault,
  NoResults,
  PageActions,
  Skeleton,
  TableFilters,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import useListFilters from "@hooks/useListFilters";
import { EMPLOYEE_SHEET_COLUMNS, linkStatusLabel } from "@helpers/employeeSheet";
import { DocumentNumber, escapeHtml, usePageHeader } from "@components/ow";
import RemoveEmployeeModal from "@components/RemoveEmployeeModal";
import LinkStatus from "@components/LinkStatus";
import ImportEmployeesModal from "./ImportEmployeesModal";

// Página da API por requisição (o padrão do back)
const PAGE_SIZE = 50;

// Pílulas do TableFilters: uma por vez, "Todos" sem filtro. "Ativos" depende do
// back (item 3.8 do ROADMAP_BUGS.md): na tela, ativo é tudo que não é WAITING.
const STATUS_DATA = [
  { label: "Todos", value: "" },
  { label: "Aguardando primeiro acesso", value: "WAITING" },
];

const INITIAL_FILTERS = { status: STATUS_DATA[0] };

const BREADCRUMB = [{ page: "Colaboradores", route: "/c/employees" }];

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const QrBox = styled.div`
  display: flex;
  justify-content: center;
  padding: 10px 0;

  svg {
    width: 200px;
    height: 200px;
  }
`;

const stopRowClick = (event) => event.stopPropagation();

const linkOf = (employee) => employee.userCompanies?.[0] || {};

export default function Employees() {
  const navigate = useNavigate();
  const employeeService = useService("users");
  const authService = useService("auth");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [paginationData, setPaginationData] = useState({ totalRegisters: 0, list: [], totalPages: 1 });
  const [loading, setLoading] = useState(true);
  // Só a resposta da última requisição vale (buscas seguidas podem voltar fora de ordem)
  const lastRequest = useRef(0);
  // Total sem filtros: diferencia "nenhum colaborador cadastrado" de "nada com estes filtros"
  const [hasAnyEmployee, setHasAnyEmployee] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [invitationLink, setInvitationLink] = useState("");
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [employeeToRemove, setEmployeeToRemove] = useState(null);
  // "Ver colaboradores" só lê: convidar, importar e remover seguem só ADM
  const isAdmin = JSON.parse(localStorage.getItem("user_ow") || "{}")?.role === "ADM";

  const { storeFilters, filters, filter, handleSetFilters, handleClearFilters, hasFilter } = useListFilters(
    INITIAL_FILTERS,
    setPagination
  );

  usePageHeader({ title: "Colaboradores", breadcrumb: BREADCRUMB });

  const statusValue = filters.status?.value || "";
  const searchTerm = (filters.search || "").trim();
  const filtersActive = !!statusValue || !!searchTerm;

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, statusValue, searchTerm]);

  // Mesma query na lista e na exportação (sem a página)
  function filterQuery() {
    const params = ["role=EMP"];
    if (statusValue) params.push(`status=${statusValue}`);
    if (searchTerm) params.push(`search=${encodeURIComponent(searchTerm)}`);
    return params.join("&");
  }

  async function fetchEmployees() {
    const request = ++lastRequest.current;
    setLoading(true);
    try {
      const result = await employeeService.list(`${filterQuery()}&page=${pagination.pageIndex + 1}&limit=${pagination.pageSize}`);
      if (request !== lastRequest.current) return;
      if (result && result.data) {
        setPaginationData(result.data);
        if (!filtersActive) setHasAnyEmployee(result.data.totalRegisters > 0);
      } else {
        throw new Error("Erro ao carregar colaboradores");
      }
    } catch (err) {
      if (request !== lastRequest.current) return;
      toast.error(err?.response?.data?.msg || err?.message || "Erro ao carregar colaboradores");
      setPaginationData({ totalRegisters: 0, list: [], totalPages: 1 });
    }
    setLoading(false);
  }

  // Limpa a busca e também a situação (o "Limpar filtros" do vazio)
  function clearAll() {
    handleClearFilters();
    storeFilters.setFilters({ status: STATUS_DATA[0] });
  }

  async function openInvite() {
    setShowInvite(true);
    setInvitationLink("");
    setInvitationLoading(true);
    try {
      const result = await authService.generateInvitation();
      if (result && !result.error && result.token) {
        setInvitationLink(window.location.origin + "/register/e/" + result.token);
      } else if (!result?.error) {
        // No erro, o service já mostra o toast
        toast.error("Erro ao gerar link de convite");
      }
    } catch (err) {
      toast.error("Erro ao gerar link de convite");
    }
    setInvitationLoading(false);
  }

  function closeInvite() {
    setShowInvite(false);
    setInvitationLink("");
  }

  async function handleExportExcel() {
    setLoadingExport(true);
    try {
      const result = await employeeService.exportAll(filterQuery());
      // exportAll já exibe o erro
      if (!result || result.error) {
        setLoadingExport(false);
        return;
      }

      const employees = result.data || [];
      if (employees.length === 0) {
        toast.info("Nenhum colaborador encontrado para exportar");
        setLoadingExport(false);
        setShowExportModal(false);
        return;
      }

      // Mesmos cabeçalhos do modelo de importação; as colunas extras no fim são
      // ignoradas se a planilha for reimportada.
      const sheetData = employees.map((emp) => {
        const link = linkOf(emp);
        const values = {
          name: emp.name,
          email: emp.email,
          document: (emp.document || "").replace(/\D/g, ""),
          phone: (emp.phone || "").replace(/\D/g, ""),
          job: link.job,
          corporate_email: link.corporate_email,
          erp_code: link.erp_code,
        };
        const row = {};
        EMPLOYEE_SHEET_COLUMNS.forEach((c) => {
          row[c.label] = values[c.key] || "";
        });
        row["Situação do cadastro"] = linkStatusLabel(link.status);
        row["Data de cadastro"] = link.created_at ? new Date(link.created_at).toLocaleDateString("pt-BR") : "";
        return row;
      });

      const ws = utils.json_to_sheet(sheetData);
      ws["!cols"] = [{ wch: 35 }, { wch: 35 }, { wch: 18 }, { wch: 15 }, { wch: 25 }, { wch: 35 }, { wch: 15 }, { wch: 28 }, { wch: 16 }];

      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, "Colaboradores");

      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2, "0")}_${String(now.getMonth() + 1).padStart(2, "0")}_${now.getFullYear()}`;
      writeFileXLSX(wb, `colaboradores_${dateStr}.xlsx`);

      toast.success("Exportação realizada com sucesso!");
      setShowExportModal(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Erro ao exportar colaboradores");
    }
    setLoadingExport(false);
  }

  const detailsRoute = (employee) => `/c/employe/${employee.id}`;

  const columns = [
    {
      id: "employee",
      header: "Colaborador",
      size: 300,
      maxSize: 380,
      cell: ({ row }) => (
        <div>
          <Text $text={escapeHtml(row.original.name || "-")} $variant="paragraph_small_bold" $color="N900" />
          <Text $text={escapeHtml(row.original.email || "")} $variant="caption_medium" $color="N400" />
        </div>
      ),
    },
    {
      id: "document",
      header: "Documento",
      size: 210,
      cell: ({ row }) =>
        row.original.document ? (
          // O botão de revelar o CPF não abre o detalhe
          <span onClick={stopRowClick}>
            <DocumentNumber value={row.original.document} hidden />
          </span>
        ) : (
          "-"
        ),
    },
    { id: "job", header: "Cargo", size: 180, cell: ({ row }) => linkOf(row.original).job || "-" },
    { id: "erp", header: "Código ERP", size: 130, cell: ({ row }) => linkOf(row.original).erp_code || "-" },
    { id: "status", header: "Situação", size: 220, cell: ({ row }) => <LinkStatus status={linkOf(row.original).status} /> },
    {
      id: "actions",
      // O "Ações" da lib tem 70px; abaixo disso a seta sai da célula
      size: 80,
      minSize: 80,
      maxSize: 80,
      align: "right",
      cell: ({ row }) => (
        <DropdownAction
          options={[
            { label: "Ver detalhes", action: () => navigate(detailsRoute(row.original)) },
            {
              label: "Remover colaborador",
              action: () => setEmployeeToRemove(row.original),
              isImportant: true,
              show: isAdmin,
            },
          ]}
        />
      ),
    },
  ];

  const noEmployeesAtAll = !loading && !filtersActive && paginationData.totalRegisters === 0;

  const noResults = noEmployeesAtAll ? (
    <NoResults
      notResultText="Nenhum colaborador cadastrado"
      includeText={isAdmin ? "Convidar colaborador" : undefined}
      includeAction={isAdmin ? openInvite : undefined}
    />
  ) : (
    <NoResults notResultText="Nenhum colaborador encontrado" includeText="Limpar filtros" includeAction={clearAll} />
  );

  const exportFilters = [
    statusValue ? `situação "${filters.status.label}"` : null,
    searchTerm ? `busca "${searchTerm}"` : null,
  ].filter(Boolean);

  return (
    <ContentPage>
      {isAdmin ? (
        <PageActions>
          <Button $text="Importar" $variant="outline" $action={() => setShowImport(true)} />
          <Button $text="Convidar colaborador" $variant="primary" $action={openInvite} />
        </PageActions>
      ) : null}

      <ContentBox>
        {/* Sem nenhum colaborador, não há o que filtrar */}
        {hasAnyEmployee !== false || filtersActive ? (
          <TableFilters
            storeFilters={storeFilters}
            searchTerm="search"
            searchPlaceholder="Buscar por nome, CPF/CNPJ ou código ERP"
            statusData={STATUS_DATA}
            filter={filter}
            handleSetFilters={handleSetFilters}
            handleClearFilters={handleClearFilters}
            setStatePagination={setPagination}
            openFilter={() => {}}
            disableFilter
            exportData={() => setShowExportModal(true)}
            hasFilter={hasFilter}
          />
        ) : null}

        <FullTable
          data={paginationData.list}
          columns={columns}
          rowCount={paginationData.totalRegisters}
          pagination={pagination}
          setPagination={setPagination}
          getRowId={(row) => String(row.id)}
          isLoading={loading}
          onRowClick={(employee) => navigate(detailsRoute(employee))}
          noResults={noResults}
        />
      </ContentBox>

      {showInvite && (
        <ModalDefault
          $showModal={showInvite}
          $setShowModal={closeInvite}
          $size="medium"
          $title="Convidar colaborador"
          $contentBackground="N0"
          $footer={<Button $text="Fechar" $variant="outline" $action={closeInvite} $noWidth />}
        >
          <Stack>
            <Text
              $text="Envie o QR code ou o link para o colaborador se cadastrar na plataforma."
              $variant="paragraph_small_medium"
              $color="N700"
            />
            {invitationLoading ? (
              <Skeleton $height={260} />
            ) : invitationLink ? (
              <>
                <QrBox>
                  <QRCode value={invitationLink} viewBox="0 0 256 256" />
                </QrBox>
                <InputCopy $label="Link de convite" defaultValue={invitationLink} $textCopy="Copiar link" $textCopied="Link copiado!" />
              </>
            ) : null}
          </Stack>
        </ModalDefault>
      )}

      {showImport && (
        <ImportEmployeesModal
          onClose={(imported) => {
            setShowImport(false);
            if (imported) fetchEmployees();
          }}
        />
      )}

      {showExportModal && (
        <ModalDefault
          $showModal={showExportModal}
          $setShowModal={() => !loadingExport && setShowExportModal(false)}
          $noClose={loadingExport}
          $size="medium"
          $title="Exportar colaboradores"
          $contentBackground="N0"
          $footer={
            <>
              <Button
                $text="Cancelar"
                $variant="outline"
                $action={() => setShowExportModal(false)}
                $isDisabled={loadingExport}
                $noWidth
              />
              <Button $text="Exportar" $variant="primary" $action={handleExportExcel} $isLoading={loadingExport} $noWidth />
            </>
          }
        >
          <Text
            $text={
              exportFilters.length
                ? "Deseja exportar para uma planilha Excel os colaboradores com os filtros ativos (<strong>%s</strong>)?"
                : "Deseja exportar para uma planilha Excel todos os colaboradores da empresa?"
            }
            $replaces={exportFilters.length ? [escapeHtml(exportFilters.join(", "))] : undefined}
            $variant="paragraph_small_medium"
            $color="N700"
            $colorEmphasis="N900"
          />
        </ModalDefault>
      )}

      {employeeToRemove ? (
        <RemoveEmployeeModal
          employee={employeeToRemove}
          onClose={() => setEmployeeToRemove(null)}
          onRemoved={() => {
            setEmployeeToRemove(null);
            fetchEmployees();
          }}
        />
      ) : null}
    </ContentPage>
  );
}
